import { applyRevision } from "@/data-preparation-tool/editing/apply-revision";
import { revisionToEdit } from "@/data-preparation-tool/inspect-and-publish/refine-and-publish-utils";
import { loadRevisionDataThunk } from "@/data-preparation-tool/loading/load-revision-data-thunk";
import { selectValidRevisionEntities } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import {
  selectRegistrationRevision,
  selectRevisionEntitySiblings,
} from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { addEntities } from "@/data-preparation-tool/store/revision/revision-slice";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { ActionTextField, NoTranslate } from "@faro-lotv/flat-ui";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  ProjectApi,
  RegistrationRevision,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { MenuItem } from "@mui/material";
import { JSX, useCallback, useMemo } from "react";

// Forbidden Windows characters
const FORBIDDEN_WINDOWS_CHARACTERS = new Set([
  "<",
  ">",
  ":",
  '"',
  "/",
  "\\",
  "|",
  "?",
  "*",
]);

const WINDOWS_RESERVED_NAMES = new Set<string>([
  "CON",
  "PRN",
  "AUX",
  "NUL",

  "COM1",
  "COM2",
  "COM3",
  "COM4",
  "COM5",
  "COM6",
  "COM7",
  "COM8",
  "COM9",
  "COM¹",
  "COM²",
  "COM³",

  "LPT1",
  "LPT2",
  "LPT3",
  "LPT4",
  "LPT5",
  "LPT6",
  "LPT7",
  "LPT8",
  "LPT9",
  "LPT¹",
  "LPT²",
  "LPT³",
] as const);

const MAX_NAME_LENGTH = 260;
const UNICODE_DOT = 46;

// ASCII control code boundaries
const ASCII_CONTROL_CODE_MAX = 31;
const ASCII_EXTENDED_CONTROL_CODE_MIN = 127;
const ASCII_EXTENDED_CONTROL_CODE_MAX = 159;

type EditNameContextMenuActionItemProps = {
  entity: CaptureTreeEntityRevision;
  disabled?: boolean;
  isInitiallyEditing?: boolean;
  onNameEdited(): void;
};

/**
 * @returns An input field to edit the name of a entity in the capture tree
 */
export function EditNameContextMenuActionItem({
  entity,
  disabled,
  isInitiallyEditing,
  onNameEdited,
}: EditNameContextMenuActionItemProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const { projectApiClient } = useApiClientContext();
  const revisionEntities = useAppSelector(selectValidRevisionEntities);
  const siblingEntities = useAppSelector(selectRevisionEntitySiblings(entity));
  const siblingEntityNames = useMemo(
    () => siblingEntities.map((child) => child.name),
    [siblingEntities],
  );

  const onChangeName = useCallback(
    async (newName: string) => {
      const sanitizedName = sanitizeName(newName);
      if (sanitizedName === entity.name) {
        // If the name hasn't changed, we can just call the callback and return early.
        onNameEdited();
        return;
      }
      if (validateScanTreeElementName(sanitizedName, siblingEntityNames)) {
        return;
      }

      const state = getState();
      const currentRevision = selectRegistrationRevision(state);
      if (!currentRevision) return;

      const revisionEntityClone = {
        ...entity,
        name: sanitizedName,
      };

      await renameEntity(
        projectApiClient,
        currentRevision,
        state,
        revisionEntityClone,
      );

      dispatch(addEntities([...revisionEntities, revisionEntityClone]));
      await dispatch(loadRevisionDataThunk({ projectApiClient }));

      onNameEdited();
    },
    [
      siblingEntityNames,
      getState,
      entity,
      projectApiClient,
      dispatch,
      revisionEntities,
      onNameEdited,
    ],
  );
  return (
    <MenuItem
      key="editName"
      disabled={disabled}
      // styling to have the cursor pointer behaviour in alignment with the viewer
      sx={{ cursor: "auto", userSelect: "auto" }}
    >
      <NoTranslate>
        <ActionTextField
          inputText={entity.name}
          isInitiallyEditing={isInitiallyEditing}
          onConfirmButtonClick={onChangeName}
          validate={(name) =>
            validateScanTreeElementName(sanitizeName(name), siblingEntityNames)
          }
          fullWidth
          disableGutters
        />
      </NoTranslate>
    </MenuItem>
  );
}

/**
 * Renames the given entity in a dedicated revision and applies it to the draft.
 *
 * @param projectApiClient client to talk to the project API
 * @param currentRevision the currently active registration revision (the draft)
 * @param state the current application state
 * @param entityToRename scan/cluster entity, with desired new `name`.
 */
async function renameEntity(
  projectApiClient: ProjectApi,
  currentRevision: RegistrationRevision,
  state: RootState,
  entityToRename: CaptureTreeEntityRevision,
): Promise<void> {
  const editRevision = await revisionToEdit(
    currentRevision,
    projectApiClient,
    state,
  );

  if (entityToRename.type === CaptureTreeEntityType.cluster) {
    await projectApiClient.createOrUpdateClusterEntitiesForRegistrationRevision(
      {
        registrationRevisionId: editRevision.id,
        requestBody: [{ id: entityToRename.id, name: entityToRename.name }],
      },
    );
  } else {
    await projectApiClient.createOrUpdateScanEntitiesForRegistrationRevision({
      registrationRevisionId: editRevision.id,
      requestBody: [{ id: entityToRename.id, name: entityToRename.name }],
    });
  }

  await applyRevision({ revision: editRevision, projectApiClient });
}

/**
 * Removes leading/trailing spaces and invisible control characters in the given name
 *
 * @param rawName The name entered by the user, which may contain unwanted characters
 * @returns The sanitized name
 */
export function sanitizeName(rawName: string): string {
  if (!rawName) return rawName;

  // Remove control characters
  const withoutControlChars = Array.from(rawName)
    .filter((char) => {
      const code = char.codePointAt(0);
      if (code === undefined) {
        return false;
      }

      const isAsciiControl = code <= ASCII_CONTROL_CODE_MAX;
      const isC1Control =
        code >= ASCII_EXTENDED_CONTROL_CODE_MIN &&
        code <= ASCII_EXTENDED_CONTROL_CODE_MAX;
      return !isAsciiControl && !isC1Control;
    })
    .join("");

  return (
    withoutControlChars
      // Remove leading & trailing whitespace (including NBSP)
      .trim()
  );
}

/**
 * Validates a new element name.
 * The name is required to be suitable for a file name, using the rules found at:
 * https://learn.microsoft.com/en-us/windows/win32/fileio/naming-a-file
 *
 * @param name The name to validate
 * @param siblingEntityNames An optional list of sibling entity names to check for duplicate names
 * @returns An error message if the name is invalid, or undefined if the name is valid
 */
export function validateScanTreeElementName(
  name: string,
  siblingEntityNames?: string[],
): string | undefined {
  const chars = Array.from(name);

  if (chars.length === 0) {
    return "Name cannot be empty.";
  }

  if (chars.length > MAX_NAME_LENGTH) {
    return `Name cannot be longer than ${MAX_NAME_LENGTH} characters.`;
  }

  // Disallow names ending with dot.
  const lastChar = chars.at(-1);
  if (lastChar === undefined) {
    return "Invalid name.";
  }

  const lastCode = lastChar.codePointAt(0);
  if (lastCode === UNICODE_DOT) {
    return "Name cannot end with a dot.";
  }

  for (const char of chars) {
    // Disallow forbidden characters
    if (FORBIDDEN_WINDOWS_CHARACTERS.has(char)) {
      return 'Name cannot contain any of the following characters: < > : " / \\ | ? *';
    }
  }

  if (isWindowsReservedName(name)) {
    return "Name cannot be a reserved Windows name.";
  }

  if (siblingEntityNames?.includes(name)) {
    return "Name already exists in this cluster.";
  }
  return undefined;
}

function isWindowsReservedName(name: string): boolean {
  // Extract base name (before first dot)
  const baseName = name.split(".")[0].trim().toUpperCase();

  return WINDOWS_RESERVED_NAMES.has(baseName);
}
